<template>
  <textarea
    :id="id"
    class="ds-textarea"
    :class="{ invalid }"
    :value="value"
    :rows="rows"
    :style="autoGrowStyle"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="invalid ? 'true' : 'false'"
    @input="$emit('input', $event.target.value)"
    @blur="$emit('blur', $event)"
  />
</template>

<script>
// DsInput's multi-line twin: same field treatment, same invalid and focus rules.
export default {
  name: 'DsTextarea',
  props: {
    value: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: null,
    },
    rows: {
      type: [String, Number],
      default: 3,
    },
    // Set to grow the box with its content, from `rows` up to this many, then scroll.
    // Mirrors b-form-textarea's max-rows, which is what this replaced on the reviews page.
    maxRows: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { grownHeight: null }
  },
  computed: {
    autoGrowStyle() {
      if (!this.grownHeight) return null
      // Once auto-grown, the handle would fight the measured height.
      return { height: `${this.grownHeight}px`, resize: 'none' }
    },
  },
  watch: {
    value() {
      this.$nextTick(this.measure)
    },
    // Without these, dropping maxRows back to null would leave the last measured height
    // (and resize: none) stuck on the element forever.
    maxRows: 'measure',
    rows: 'measure',
  },
  mounted() {
    this.measure()
    // A narrower viewport rewraps the text, so the measured height has to be redone.
    window.addEventListener('resize', this.measure)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    // Measures the content, then caps at maxRows. Reading scrollHeight needs the height
    // released first, or it only ever reports the height we last set.
    measure() {
      const el = this.$el
      if (!this.maxRows) {
        // Release the height, so the element goes back to honouring `rows` and the handle.
        this.grownHeight = null
        return
      }
      this.grownHeight = null
      this.$nextTick(() => {
        // jsdom reports 0 for scrollHeight, so there is nothing to measure under test.
        if (!el.scrollHeight) return
        const style = window.getComputedStyle(el)
        const lineHeight = parseFloat(style.lineHeight) || 21
        const chrome = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
          + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
        const max = lineHeight * Number(this.maxRows) + chrome
        const min = lineHeight * Number(this.rows) + chrome
        const wanted = el.scrollHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
        this.grownHeight = Math.round(Math.min(Math.max(wanted, min), max))
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.ds-textarea {
  @include ds-field;

  display: block;
  width: 100%;
  // An explicit value, not a ratio: measure() needs a resolvable px line-height.
  line-height: 21px;
  // Horizontal dragging would break the field out of its column.
  resize: vertical;
  overflow-y: auto;

  &::placeholder { color: $c-text-ter; }

  &:focus { @include ds-focus-ring; }

  &.invalid {
    border-color: $c-danger;
    background: $c-danger-light;

    &:focus { @include ds-focus-ring($c-danger); }
  }

  &:disabled {
    background: $c-bg;
    color: $c-text-sec;
    cursor: not-allowed;
  }
}
</style>
